import React, { useState, useEffect, useRef } from "react";
import { Col, Container, Form, Row } from "reactstrap";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import { FormProvider, useForm } from "react-hook-form";
import AuthLeftPanel from "../AuthLeftPanel";
import loginWordmarkWhite from "../../../media/img/auth/login-wordmark-white.png";
import FormInput from "../../../toolkit/formInput";
import PrimaryButton from "../../../toolkit/primaryButton";
import ErrorAlert from "../../../toolkit/errorAlert";
import ModalFrame from "../../../toolkit/modalFrame";
import PageTransition from "../../../toolkit/pageTransition";
import { MobileLogo, AuthFormCol, AuthContentCol } from "../shared";
import {
  emailRules,
} from "../../../utils/validation/validationRules";
import { connect } from "react-redux";
import * as userActions from "../../../utils/actions/user";
import {
  executeAdminLogin,
  executeAdminLoginComplete,
} from "../../../utils/fetch";
import { buildAdminSessionFromLoginResponse } from "../../../utils/adminAuthSession";
import { redirectAfterLoginWithPhoneGate } from "../../../utils/adminPhonePrompt";
import { seedSessionAccessToken } from "../../../utils/firebaseToken";
import AdminOtpPanel from "../../../toolkit/adminOtpPanel";
import otpPanelStyles from "../../../toolkit/adminOtpPanel/styles.scss";
import { serviceCaller, ServiceCallerProps } from "../../../Admin/composition";
import ILoginResponse from "../../../types/services/ILoginResponse";
import ICharity from "../../../types/services/ICharity";
import { withFirebase } from "react-redux-firebase";
import { resolvePostLoginDestination } from "../../../utils/onboarding/onboardingAccess";
import { withRouter, RouterProps } from "../../../utils/withRouter";
import { Link } from "react-router-dom";
import {
  CONFIG_LOAD_ERROR_MESSAGE,
  useEffectiveGlobalConfig,
} from "../../../utils/useEffectiveGlobalConfig";
import {
  ALPHA_LOGIN_CALLOUT,
  ALPHA_LOGIN_REDIRECT_MODAL,
  goToViewSparkSignIn,
  shouldOpenAlphaLoginRedirect,
  shouldShowAlphaLoginSafeguard,
} from "./alphaLoginSafeguard";

interface LoginForm {
  email: string;
  password: string;
}

interface Props extends ServiceCallerProps, RouterProps {
  firebase: any;
  firebaseData: any;
  loginUser: (
    user: IUser,
    currentCharity: ICharity,
    charities: Array<ICharity>
  ) => void;
}

const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please sign in again.";

const LoginPage: React.FC<Props> = ({
  firebase,
  firebaseData,
  loginUser,
  config,
  navigate,
  location
}) => {
  const { effectiveConfig, isConfigReady, configLoadError } =
    useEffectiveGlobalConfig(config, firebase);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(false);
  const [loginStep, setLoginStep] = useState<"password" | "otp">("password");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [otpSmsEligible, setOtpSmsEligible] = useState(false);
  const [otpDeliveryChannel, setOtpDeliveryChannel] = useState<"email" | "sms">("email");
  const [showVerificationSuccess, setShowVerificationSuccess] = useState(false);
  const [alphaRedirectOpen, setAlphaRedirectOpen] = useState(false);
  const showAlphaLoginSafeguard = shouldShowAlphaLoginSafeguard();

  const firebaseDataRef = useRef(firebaseData);
  useEffect(() => {
    firebaseDataRef.current = firebaseData;
  }, [firebaseData]);

  const methods = useForm<LoginForm>({
    defaultValues: {
      email: "",
      password: ""
    }
  });

  useEffect(() => {
    if (configLoadError) {
      setLoginError(true);
      setErrorMessage(CONFIG_LOAD_ERROR_MESSAGE);
      return;
    }
    if (isConfigReady) {
      setLoginError(false);
      setErrorMessage("");
    }
  }, [configLoadError, isConfigReady]);

  useEffect(() => {
    if (location.state?.emailVerified) {
      setShowVerificationSuccess(true);
    }
  }, [location.state]);

  const onSubmit = (data: LoginForm) => {
    setIsSubmitting(true);
    setLoginError(false);
    setSessionExpiredNotice(false);
    login(data);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (params.get("session") === "expired") {
      setSessionExpiredNotice(true);
    }
  }, [location.search]);

  const finishWithLoginResponse = (response: ILoginResponse) => {
    return firebase
      .login({ token: response.token })
      .then(async () => {
        await seedSessionAccessToken();
        const { user, realms } = buildAdminSessionFromLoginResponse(
          response,
          loginUser
        );
        if (user.isDonationReader) {
          setIsSubmitting(false);
          navigate("/allDonations");
          return;
        }
        let destination = "/contacts";
        const charityId = realms[0];
        if (charityId) {
          try {
            const snapshot = await firebase
              .database()
              .ref(`${charityId}/onboardingRequired`)
              .once("value");
            destination = resolvePostLoginDestination(
              user,
              charityId,
              firebaseDataRef.current,
              snapshot.val() === true
            );
          } catch {
            destination = resolvePostLoginDestination(
              user,
              charityId,
              firebaseDataRef.current
            );
          }
        }
        setIsSubmitting(false);
        await redirectAfterLoginWithPhoneGate(
          navigate,
          firebase,
          user.id,
          destination,
          realms[0] || ""
        );
      })
      .catch((err: Error) => {
        setLoginError(true);
        setErrorMessage(err?.message || "Login failed.");
        setIsSubmitting(false);
      });
  };

  const login = (data: LoginForm) => {
    if (!effectiveConfig?.authServiceUrl) {
      setErrorMessage("Configuration not loaded.");
      setLoginError(true);
      setIsSubmitting(false);
      return;
    }
    executeAdminLogin(effectiveConfig.authServiceUrl, data)
      .then(result => {
        if (result.stage === "TOKEN" && result.loginResponse) {
          return finishWithLoginResponse(result.loginResponse);
        }
        if (result.stage === "CHALLENGE" && result.challengeId) {
          setIsSubmitting(false);
          setLoginStep("otp");
          setChallengeId(result.challengeId);
          setOtpSmsEligible(result.smsEligible === true);
          setOtpDeliveryChannel(result.deliveryChannel === "sms" ? "sms" : "email");
          setLoginError(false);
          setErrorMessage("");
          return;
        }
        throw new Error("Unexpected login response.");
      })
      .catch(error => {
        setErrorMessage(error.message);
        setLoginError(true);
        setIsSubmitting(false);
        if (shouldOpenAlphaLoginRedirect("passwordRequest")) {
          setAlphaRedirectOpen(true);
        }
      });
  };

  const onOtpVerified = async (completionToken: string) => {
    if (!effectiveConfig?.authServiceUrl) {
      throw new Error("Configuration not loaded.");
    }
    setIsSubmitting(true);
    setLoginError(false);
    try {
      const result = await executeAdminLoginComplete(
        effectiveConfig.authServiceUrl,
        completionToken
      );
      if (result.stage === "TOKEN" && result.loginResponse) {
        await finishWithLoginResponse(result.loginResponse);
        return;
      }
      throw new Error("Login could not be completed.");
    } catch (error: any) {
      setErrorMessage(error?.message || "Login failed.");
      setLoginError(true);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonBusy = !isConfigReady || isSubmitting;

  const backToPassword = () => {
    setLoginStep("password");
    setChallengeId(null);
    setOtpSmsEligible(false);
    setOtpDeliveryChannel("email");
    setLoginError(false);
    setErrorMessage("");
  };

  return (
    <Container fluid className="fullscreenFixed">
      <Row className="h-100">
        <AuthLeftPanel
          title="Welcome Back"
          subtitle="Login to access your dashboard"
          centeredWordmarkSrc={loginWordmarkWhite}
        />
        <AuthContentCol className={s.rightPanel}>
          <PageTransition>
          <MobileLogo />
          {showAlphaLoginSafeguard && (
            <Row className="justify-content-center mt-3 mt-md-5">
              <AuthFormCol>
                <div className={s.itLoginCallout} role="note">
                  <div className={s.itLoginCalloutHeading}>
                    <i
                      className={`fa-solid fa-triangle-exclamation ${s.itLoginCalloutIcon}`}
                      aria-hidden="true"
                    />
                    <p className={s.itLoginCalloutHeadline}>
                      {ALPHA_LOGIN_CALLOUT.headline}
                    </p>
                  </div>
                  <p className={s.itLoginCalloutBody}>{ALPHA_LOGIN_CALLOUT.body}</p>
                  <PrimaryButton
                    type="button"
                    className="w-100"
                    onClick={goToViewSparkSignIn}
                  >
                    {ALPHA_LOGIN_CALLOUT.action}
                  </PrimaryButton>
                </div>
              </AuthFormCol>
            </Row>
          )}
          <Row className="justify-content-center mt-md-5 mt-2">
            <AuthFormCol className={`text-lg-start`}>
              {showVerificationSuccess && (
                <div className={`${s.successMessage} mb-3 d-flex align-items-center justify-content-between`}>
                  <div>
                    <i className="fa-solid fa-circle-check me-2"></i>
                    Email verified successfully! Please log in to continue.
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowVerificationSuccess(false)}
                    style={{ opacity: 0.5 }}
                  ></button>
                </div>
              )}
            </AuthFormCol>
          </Row>
          <Row className="justify-content-center mt-md-5 mt-0">
            <AuthFormCol className={`text-center`}>
              {loginStep === "otp" ? (
                <div className={otpPanelStyles.backRowAboveTitle}>
                  <button
                    type="button"
                    className={otpPanelStyles.linkButton}
                    onClick={backToPassword}
                    disabled={isButtonBusy}
                  >
                    Back
                  </button>
                </div>
              ) : null}
              <h2 className={s.title}>
                {loginStep === "otp" ? "Verify it's you" : "Welcome Back"}
              </h2>
            </AuthFormCol>
          </Row>
          <FormProvider {...methods}>
            {loginStep === "otp" && effectiveConfig?.authServiceUrl && challengeId ? (
              <Row className="mt-4 justify-content-center">
                <AuthFormCol>
                  {loginError && (
                    <ErrorAlert
                      message={errorMessage || "Verification failed."}
                    />
                  )}
                  <AdminOtpPanel
                    authServiceUrl={effectiveConfig.authServiceUrl}
                    challengeId={challengeId}
                    smsEligible={otpSmsEligible}
                    initialChannel={otpDeliveryChannel}
                    onVerified={onOtpVerified}
                    onBack={backToPassword}
                    embedBackLink={false}
                    showVerifiedSuccessScreen
                  />
                </AuthFormCol>
              </Row>
            ) : (
            <Form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
              <Row className="mt-4 justify-content-center">
                <AuthFormCol>
                  <FormInput
                    name="email"
                    type="email"
                    rules={emailRules}
                    icon={"fa-regular fa-envelope"}
                    placeholder="Email"
                  />
                </AuthFormCol>
              </Row>
              <Row className="mt-3 justify-content-center">
                <AuthFormCol>
                  <FormInput
                    name="password"
                    type="password"
                    icon="fa-solid fa-lock"
                    rules={{ required: "Password is required" }}
                    placeholder="Password"
                  />
                </AuthFormCol>
              </Row>
              {sessionExpiredNotice && (
                <Row className="mt-3 justify-content-center">
                  <AuthFormCol>
                    <ErrorAlert message={SESSION_EXPIRED_MESSAGE} />
                  </AuthFormCol>
                </Row>
              )}
              {loginError && (
                <Row className="mt-3 justify-content-center">
                  <AuthFormCol>
                    <ErrorAlert
                      message={errorMessage || "Login failed. Please try again."}
                    />
                  </AuthFormCol>
                </Row>
              )}
              <Row className="mt-4 justify-content-center">
                <AuthFormCol>
                  <PrimaryButton
                    type="submit"
                    className={`w-100`}
                    disabled={isButtonBusy}
                  >
                    {isButtonBusy ? "Loading..." : "Login"}
                  </PrimaryButton>
                </AuthFormCol>
              </Row>
              <Row className="mt-3 justify-content-center">
                <AuthFormCol className="text-center">
                  <Link to="/forgot" className={s.forgotPasswordLink}>
                    Forgot password?
                  </Link>
                </AuthFormCol>
              </Row>
            </Form>
            )}
          </FormProvider>
          {showAlphaLoginSafeguard && (
            <ModalFrame
              title={ALPHA_LOGIN_REDIRECT_MODAL.title}
              isOpen={alphaRedirectOpen}
              toggle={() => undefined}
              dismissible={false}
              centered
            >
              <p className={s.itLoginRedirectBody}>
                {ALPHA_LOGIN_REDIRECT_MODAL.body}
              </p>
              <PrimaryButton
                type="button"
                className="w-100"
                onClick={goToViewSparkSignIn}
              >
                {ALPHA_LOGIN_REDIRECT_MODAL.action}
              </PrimaryButton>
            </ModalFrame>
          )}
          </PageTransition>
        </AuthContentCol>
      </Row>
    </Container>
  );
};

const mapStateToProps = state => {
  const { firebase } = state;
  return {
    firebaseData: firebase.data || {},
  };
};

const mapDispatchToProps = dispatch => ({
  loginUser: (user = null, currentCharity = null, charities = null) => {
    dispatch(userActions.loginUser(user, currentCharity, charities));
  },
});

export default withFirebase(
  serviceCaller(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(withRouter(withStyles(s)(LoginPage)))
  )
);
